import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { path, isEmpty, equals } from 'ramda';
import React, { Fragment, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import useDetectDiffInChanges from '../../../utils/hooks/useDetectDiffInChanges';
import { useForm } from '../../../utils/hooks/useForm';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import { DisplayFlexS } from '../../../utils/stylesHelpers';
import { SOCIAL_MEDIA } from '../../constants/filters';
import { SCREEN_SIZES } from '../../constants/screenSizes';
import { InputLabel, SelectS, Text, TextInput } from '../common';

import FieldComment from './common/fieldComment';
import SaveBtn from './common/saveBtn';
import { AddNewFieldBtnWrapS, InputWrapper, BlueCloseIcon, SaveButtonWrapper } from './companyEditS';

const validate = ({ socialMedia, base: { website } }) => {
  const errors = { socialMedia: {}, base: {} };
  const websitePattern = /^$|(www\.)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}/;

  const validationPatterns = {
    [SOCIAL_MEDIA.FACEBOOK]: /^(https?:\/\/)?([\w-]+\.)?(facebook|fb)\.com\/?([\w-]*)?(\.[\w-]*)?/g,
    [SOCIAL_MEDIA.LINKED_IN]: /^(https?:\/\/)?([\w]+\.)?linkedin\.com\/(company)?(?:\/*)([\w.-]*)/g,
    [SOCIAL_MEDIA.SKYPE]: /^(?:[a-z][a-z0-9.,-_]{6,32})$/g,
    [SOCIAL_MEDIA.WHATS_APP]: /^\+\d+$/g,
    [SOCIAL_MEDIA.TWITTER]: /^(https?:\/\/)?(twitter|x)\.com\/?([\w-]*)?(\.[\w-]*)?$/g,
    [SOCIAL_MEDIA.GOOGLE_MY_BUSINESS]: /^(https?:\/\/)?(g\.page|maps\.app\.goo\.gl)\/[\w-]+$/g,
    [SOCIAL_MEDIA.YOUTUBE]:
      /^(http(s)?:\/\/)?([\w]+\.)?youtube.com\/?(\/(?:channel\/[\w\-]+|@[\w.\-]+|(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)?)(\S+)?$/g,
  };

  if (website && !websitePattern.test(website)) {
    errors.base.website = t('companyEdit.errorMessages.website');
  }

  socialMedia
    .filter(({ type }) => !!type)
    .forEach(({ type, value }) => {
      if ((type && !value) || (validationPatterns[type] && !validationPatterns[type].test(value))) {
        errors.socialMedia[type] = t([
          `companyEdit.errorMessages.socialMedia.${type}`,
          'companyEdit.errorMessages.socialMedia.default',
        ]);
      }
    });

  if (isEmpty(errors.socialMedia)) {
    // have to add this line in order to return empty object if no errors was added.
    delete errors.socialMedia;
  }
  if (isEmpty(errors.base)) {
    delete errors.base;
  }

  return errors;
};

const getInitialData = (company) => {
  const socialMediaConverted = Object.keys(path(['socialMedia'], company) || {}).map((key) => ({
    type: key,
    value: company.socialMedia[key],
  }));

  return {
    socialMedia: socialMediaConverted.length ? socialMediaConverted : [{ type: '', value: '' }],
    base: {
      website: path(['base', 'website'], company) || '',
    },
  };
};

const SocialSettings = ({ isAdmin, updateCompany, setActiveSettingType }) => {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company.companyItem);
  const loading = useSelector((state) => state.company.loading);
  const { width: windowWidth } = useWindowSize();

  const [initialValues, setInitialValues] = useState(getInitialData(company));

  useEffect(() => {
    setInitialValues(getInitialData(company));
  }, [company]);

  const handleSubmit = (values) => {
    const socialMedia = values.socialMedia.reduce((acc, social) => {
      if (social.type && social.value) {
        acc[social.type] = social.value;
      }
      return acc;
    }, {});

    dispatch(
      updateCompany(
        {
          ...company,
          base: { ...company.base, ...values.base },
          socialMedia,
        },
        isAdmin
      )
    );
  };

  const hasChanges = (values) => !equals(getInitialData(company), values);

  const {
    values,
    errors,
    touched,
    onChange: handleChange,
    onBlur: handleBlur,
    onSubmit,
    setFieldValue,
    dirty,
  } = useForm({
    initialValues,
    validate,
    onSubmit: handleSubmit,
  });

  useDetectDiffInChanges(() => hasChanges(values), onSubmit, setActiveSettingType);

  return (
    <form onSubmit={onSubmit}>
      <DisplayFlexS flexWrapScreenTablet="wrap">
        <DisplayFlexS justifyScreenMedium="center" minWidth="45%">
          <InputWrapper>
            <TextInput
              label={t('companyEdit.labels.website')}
              name="base.website"
              type="text"
              value={values.base.website}
              placeholder={t('companyEdit.socialMedia.placeholders.website')}
              isError={path(['base', 'website'], errors) && path(['base', 'website'], touched)}
              error={path(['base', 'website'], errors)}
              onChange={handleChange}
              onBlur={handleBlur}
              padding="8px 20px"
            />
          </InputWrapper>
        </DisplayFlexS>
        {windowWidth > SCREEN_SIZES.MEDIUM ? (
          <FieldComment transPath="companyEdit.fieldComment.social" marginBottomScreenTablet="20px" maxWidth="50%" />
        ) : null}
      </DisplayFlexS>

      <DisplayFlexS maxWidth="45%" marginScreenMedium="30px 0 0">
        <InputWrapper>
          <InputLabel>
            <Trans i18nKey="companyEdit.labels.socialMedia" />
          </InputLabel>
          <DisplayFlexS direction="column" directionScreenMedium="column">
            {values.socialMedia.map((socialMediaItem, i) => {
              const hasRenderAddBtn =
                values.socialMedia.length === i + 1 && values.socialMedia.length < Object.keys(SOCIAL_MEDIA).length;

              const getOptionsForSelect = () =>
                Object.values(SOCIAL_MEDIA)
                  .filter(
                    (name) =>
                      socialMediaItem.type === name || !values.socialMedia.map(({ type }) => type).includes(name)
                  )
                  .map((socialName) => (
                    <option key={socialName} value={socialName}>
                      {t(`companyEdit.socialMedia.names.${socialName}`)}
                    </option>
                  ));

              return (
                <Fragment key={i}>
                  <DisplayFlexS margin="0 0 10px 0" marginScreenMedium="0 0 22px 0">
                    <SelectS
                      height="auto"
                      width="50%"
                      value={socialMediaItem.type}
                      onChange={({ target: { value } }) => setFieldValue(`socialMedia.${i}.type`, value, false)}
                    >
                      <option value="">{t('companyEdit.selectPlaceholders.socialMedia')}</option>
                      {getOptionsForSelect()}
                    </SelectS>

                    <InputWrapper style={{ marginBottom: '0px' }}>
                      <TextInput
                        type="text"
                        name={`socialMedia.${i}.value`}
                        value={socialMediaItem.value}
                        placeholder={t([`companyEdit.socialMedia.placeholders.${socialMediaItem.type}`, ''])}
                        onChange={handleChange}
                        isError={errors.socialMedia && errors.socialMedia[socialMediaItem.type]}
                        error={errors.socialMedia && errors.socialMedia[socialMediaItem.type]}
                        onBlur={handleBlur}
                      />
                    </InputWrapper>
                    {values.socialMedia.length > 1 && (
                      <BlueCloseIcon
                        style={{ margin: '8px 0 0 10px' }}
                        onClick={() =>
                          setFieldValue(
                            'socialMedia',
                            values.socialMedia.filter((_, indexInner) => indexInner !== i),
                            false
                          )
                        }
                      />
                    )}
                  </DisplayFlexS>

                  {hasRenderAddBtn && ( // show icon after last element only
                    <AddNewFieldBtnWrapS
                      marginTopScreenDesktop="-10px"
                      marginTopScreenMedium="-15px"
                      onClick={() =>
                        setFieldValue('socialMedia', [...values.socialMedia, { type: '', value: '' }], false)
                      }
                    >
                      <Text color="red" style={{ fontSize: '18px', margin: 5 }}>
                        <FontAwesomeIcon icon="plus-circle" />
                      </Text>
                      <Text size="smallText">{t('companyEdit.addItemBtn')}</Text>
                    </AddNewFieldBtnWrapS>
                  )}
                </Fragment>
              );
            })}
          </DisplayFlexS>
        </InputWrapper>
      </DisplayFlexS>

      <SaveButtonWrapper marginTop="30" marginTopMobile="162">
        <SaveBtn loading={loading} disabled={!dirty && !hasChanges(values)} />
      </SaveButtonWrapper>
    </form>
  );
};

export default SocialSettings;
